import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ScanLine, Type, Check, X, Loader2, AlertCircle, CheckCircle2, Camera,
  Ticket, ArrowRight, Receipt, Settings, DollarSign,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from '../components/OnboardingShell';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/* ===========================
   TABS
   =========================== */
const TABS = [
  { key: 'redeem',  label: 'Redeem gift',  icon: Ticket },
  { key: 'history', label: 'Reimbursements', icon: Receipt },
  { key: 'profile', label: 'Reimbursement setup', icon: Settings },
];

/* ===========================
   PAGE
   =========================== */
const DashboardGiftsPage = () => {
  const navigate = useNavigate();
  const { token, restaurant } = useAuth() || {};
  const [tab, setTab] = useState('redeem');

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  if (!restaurant) {
    return (
      <OnboardingShell showProgress={false} testid="gifts-loading">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell showProgress={false} backFallback="/dashboard" contentMaxWidth="max-w-4xl" testid="dashboard-gifts-page">
      <div className="mb-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
          <Ticket className="w-3.5 h-3.5 text-yellow-300" />
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">NoFee Gifts</span>
        </div>
        <h1 className="mt-4 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-white">
          Accept NoFee Gift Credits.
        </h1>
        <p className="mt-3 text-[14.5px] text-white/65 leading-relaxed max-w-2xl">
          Customers pay with NoFee gift credits. NoFee reimburses you the full amount promptly via Stripe.
          You never lose money on a valid gift redemption.
        </p>
      </div>

      {/* Tab strip */}
      <div className="mt-7 flex gap-1 p-1 rounded-2xl border border-white/[0.07] bg-white/[0.02]" data-testid="gifts-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gifts-tab-${t.key}`}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[12.5px] font-bold transition-all ${
                active ? 'bg-black border border-yellow-500/40 text-yellow-200' : 'text-white/55 hover:text-white/85'
              }`}>
              <Icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === 'redeem'  && <RedeemPanel restaurant={restaurant} token={token} />}
        {tab === 'history' && <HistoryPanel restaurant={restaurant} token={token} />}
        {tab === 'profile' && <ProfilePanel restaurant={restaurant} token={token} />}
      </div>
    </OnboardingShell>
  );
};

/* ===========================
   REDEEM PANEL (validate -> redeem)
   =========================== */
const RedeemPanel = ({ restaurant, token }) => {
  const [mode, setMode] = useState('manual');      // manual | scan
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validatedGift, setValidatedGift] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);    // {gift_id, amount_cents, customer_name}
  const [redeeming, setRedeeming] = useState(false);

  const onValidate = async (codeToCheck) => {
    const c = (codeToCheck ?? code).trim().toUpperCase();
    if (!c) return;
    setValidating(true);
    setError('');
    setValidatedGift(null);
    setSuccess(null);
    try {
      const r = await axios.post(
        `${API}/api/restaurants/${restaurant.id}/gift-redemptions/validate`,
        { code: c },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.data.valid) {
        setError(r.data.error || 'Invalid gift.');
      } else {
        setValidatedGift(r.data);
        setCode(c);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Validation failed.');
    } finally {
      setValidating(false);
    }
  };

  const onRedeem = async () => {
    if (!validatedGift) return;
    setRedeeming(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/api/restaurants/${restaurant.id}/gift-redemptions/redeem`,
        { code: code.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(r.data);
      setValidatedGift(null);
      setCode('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not redeem.');
    } finally {
      setRedeeming(false);
    }
  };

  const reset = () => {
    setCode('');
    setValidatedGift(null);
    setError('');
    setSuccess(null);
  };

  // -------- POST-REDEEM SUCCESS --------
  if (success) {
    return (
      <div data-testid="redeem-success" className="text-center py-10">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/45 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-300" strokeWidth={2.2} />
        </div>
        <h2 className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl font-semibold tracking-tight">
          Redeemed.
        </h2>
        <p className="mt-2 text-[14px] text-white/60">
          <strong className="text-white">{success.customer_name || 'Customer'}</strong> paid {fmt$(success.amount_cents)} with NoFee credit.
        </p>
        <div className="mt-5 inline-block p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] text-left">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-300 font-bold mb-2">Reimbursement queued</div>
          <div className="text-[12.5px] text-white/75">
            NoFee will reimburse you <span className="text-yellow-300 font-bold">{fmt$(success.amount_cents)}</span> directly to your Stripe account.
          </div>
          <div className="mt-1.5 text-[11px] text-white/45 font-mono">Gift: {success.gift_id}</div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={reset} data-testid="redeem-another-btn"
            className="px-6 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[13px]">
            Redeem another gift
          </button>
        </div>
      </div>
    );
  }

  // -------- VALIDATED — CONFIRM STEP --------
  if (validatedGift) {
    return (
      <div data-testid="redeem-confirm" className="p-6 sm:p-8 rounded-3xl border border-yellow-500/35 bg-gradient-to-br from-yellow-500/[0.05] to-amber-500/[0.02]">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Valid gift</p>
        <div className="mt-3 text-5xl font-extrabold text-white tabular-nums">{fmt$(validatedGift.amount_cents)}</div>
        <p className="mt-3 text-[14px] text-white/75">
          From <strong className="text-white">{validatedGift.customer_name || 'a NoFee customer'}</strong>
        </p>
        <div className="mt-1 text-[11px] text-white/45 font-mono">{validatedGift.gift_id}</div>

        <div className="mt-5 p-3 rounded-xl bg-black/40 border border-white/[0.07] text-[12px] text-white/65 leading-relaxed">
          <strong className="text-white/85">Important:</strong> The customer pays {fmt$(validatedGift.amount_cents)} less at checkout. NoFee reimburses you the full {fmt$(validatedGift.amount_cents)}.
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[12.5px]">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <button onClick={onRedeem} disabled={redeeming} data-testid="redeem-confirm-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 disabled:opacity-50 text-black font-extrabold text-[14px] transition-all">
            {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {redeeming ? 'Confirming…' : `Confirm redemption · ${fmt$(validatedGift.amount_cents)}`}
          </button>
          <button onClick={reset} data-testid="redeem-cancel-btn"
            className="sm:flex-none px-5 py-4 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/65 hover:text-rose-300 font-bold text-[13px] transition-all">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // -------- INPUT (MANUAL OR SCAN) --------
  return (
    <div data-testid="redeem-input">
      {/* Mode toggle */}
      <div className="flex gap-1 p-1 rounded-xl border border-white/[0.07] bg-white/[0.02] mb-5 max-w-xs">
        <button onClick={() => setMode('manual')} data-testid="mode-manual-btn"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold transition-all ${
            mode === 'manual' ? 'bg-yellow-500/[0.10] border border-yellow-500/35 text-yellow-200' : 'text-white/55'}`}>
          <Type className="w-3.5 h-3.5" /> Enter code
        </button>
        <button onClick={() => setMode('scan')} data-testid="mode-scan-btn"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold transition-all ${
            mode === 'scan' ? 'bg-yellow-500/[0.10] border border-yellow-500/35 text-yellow-200' : 'text-white/55'}`}>
          <Camera className="w-3.5 h-3.5" /> Scan QR
        </button>
      </div>

      {mode === 'manual' ? (
        <div className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.02]">
          <label className="block text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">
            Gift code or Gift ID
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && onValidate()}
            placeholder="ABCD2345 or GFT-ABCD2345"
            maxLength={24}
            autoFocus
            data-testid="redeem-code-input"
            className="w-full px-4 py-4 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/50 outline-none text-[18px] font-mono tracking-[0.18em] text-white placeholder:text-white/30 placeholder:tracking-normal placeholder:font-sans"
          />
          <p className="mt-2 text-[11px] text-white/40">8 characters, no I/O/0/1. Letters only or with the GFT- prefix.</p>
          {error && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[12.5px]" data-testid="redeem-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <button onClick={() => onValidate()} disabled={validating || !code.trim()} data-testid="validate-gift-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white text-black hover:bg-white/90 disabled:opacity-50 font-bold text-[14px] transition-all">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {validating ? 'Validating…' : 'Validate gift'}
          </button>
        </div>
      ) : (
        <QrScanner onScan={(c) => onValidate(c)} onError={setError} />
      )}
    </div>
  );
};

/* ===========================
   QR SCANNER (uses html5-qrcode lazily)
   =========================== */
const QrScanner = ({ onScan, onError }) => {
  const containerId = 'qr-reader-container';
  const scannerRef = useRef(null);
  const [active, setActive] = useState(false);
  const [bootError, setBootError] = useState('');

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    (async () => {
      try {
        const mod = await import('html5-qrcode');
        const { Html5Qrcode } = mod;
        if (stopped) return;
        const reader = new Html5Qrcode(containerId);
        scannerRef.current = reader;
        await reader.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 240 },
          (text) => {
            // Stop after first successful scan
            reader.stop().then(() => onScan(text)).catch(() => onScan(text));
          },
          () => { /* per-frame failures are normal */ },
        );
      } catch (err) {
        setBootError(err?.message || 'Camera unavailable.');
        onError?.('Could not access camera. Use the manual code entry instead.');
      }
    })();
    return () => {
      stopped = true;
      if (scannerRef.current) {
        scannerRef.current.stop?.().catch(() => {});
      }
    };
  }, [active, onScan, onError]);

  if (!active) {
    return (
      <div className="p-8 rounded-2xl border border-white/[0.08] bg-black/30 text-center" data-testid="qr-scanner-idle">
        <Camera className="w-10 h-10 text-white/40 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-[13.5px] text-white/65 mb-4">Point your camera at the customer's QR code.</p>
        <button onClick={() => setActive(true)} data-testid="start-scanner-btn"
          className="px-6 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[13px]">
          Start camera
        </button>
      </div>
    );
  }

  return (
    <div data-testid="qr-scanner-active">
      <div id={containerId} className="rounded-2xl overflow-hidden border border-white/[0.08] bg-black"
        style={{ width: '100%', maxWidth: 420, margin: '0 auto', aspectRatio: '1 / 1' }} />
      {bootError ? (
        <p className="mt-3 text-[12.5px] text-rose-300 text-center">{bootError}</p>
      ) : (
        <p className="mt-3 text-[11.5px] text-white/40 text-center">
          Allow camera access if prompted. Scanning works best in bright light.
        </p>
      )}
      <button onClick={() => setActive(false)} data-testid="stop-scanner-btn"
        className="mt-4 mx-auto block px-5 py-2 rounded-lg border border-white/12 hover:border-white/30 text-white/65 hover:text-white text-[12.5px] font-bold">
        <X className="w-3.5 h-3.5 inline mr-1.5" /> Stop scanning
      </button>
    </div>
  );
};

/* ===========================
   HISTORY PANEL (reimbursements)
   =========================== */
const HistoryPanel = ({ restaurant, token }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], totals: {} });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/restaurants/${restaurant.id}/gift-reimbursements`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [restaurant.id, token]);

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin mx-auto" /></div>;

  const t = data.totals || {};
  return (
    <div data-testid="reimbursements-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Pending',  value: t.pending_cents || 0,  tone: 'border-amber-500/30 bg-amber-500/[0.05]' },
          { label: 'Approved', value: t.approved_cents || 0, tone: 'border-sky-500/30 bg-sky-500/[0.05]' },
          { label: 'Paid',     value: t.paid_cents || 0,     tone: 'border-emerald-500/30 bg-emerald-500/[0.05]' },
          { label: 'Rejected', value: t.rejected_cents || 0, tone: 'border-rose-500/30 bg-rose-500/[0.05]' },
        ].map(({ label, value, tone }) => (
          <div key={label} className={`p-4 rounded-2xl border ${tone}`}>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold flex items-center gap-1.5">
              <DollarSign className="w-3 h-3" /> {label}
            </div>
            <div className="mt-2 text-2xl font-extrabold tabular-nums">{fmt$(value)}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.07] bg-white/[0.015] overflow-hidden">
        {data.rows.length === 0 ? (
          <div className="p-10 text-center text-white/45 text-[14px]">No reimbursements yet. They appear as customers redeem gifts.</div>
        ) : (
          <ul className="divide-y divide-white/[0.05]">
            {data.rows.map((r) => (
              <li key={r.id} className="p-4 flex items-center justify-between gap-3" data-testid={`reimbursement-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[14px]">{fmt$(r.amount_cents)}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mt-1 text-[11.5px] text-white/45 font-mono truncate">{r.gift_id}</div>
                </div>
                <div className="text-right text-[11px] text-white/50 shrink-0">
                  <div>{r.customer_name || '—'}</div>
                  <div className="text-white/35">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const meta = {
    pending:  { label: 'Pending review',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
    approved: { label: 'Approved',        cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
    paid:     { label: 'Paid',            cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
    rejected: { label: 'Rejected',        cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
  }[status] || { label: status, cls: 'bg-white/10 text-white/55 border-white/15' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

/* ===========================
   PROFILE PANEL (Zelle)
   =========================== */
const ProfilePanel = ({ restaurant, token }) => {
  const [profile, setProfile] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    payout_method: 'zelle', zelle_email: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/restaurants/${restaurant.id}/reimbursement-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.data?.profile && Object.keys(r.data.profile).length) {
          setProfile((p) => ({ ...p, ...r.data.profile }));
        } else {
          // Pre-fill from restaurant fields when no profile yet
          setProfile((p) => ({ ...p, business_name: restaurant.name || '', phone: restaurant.phone || '' }));
        }
      } finally { setLoading(false); }
    })();
  }, [restaurant, token]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError(''); setSaved(false);
    try {
      await axios.put(`${API}/api/restaurants/${restaurant.id}/reimbursement-profile`, profile,
        { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin mx-auto" /></div>;

  const inputCls = 'w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/50 outline-none text-[14px]';
  const labelCls = 'block text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5';

  return (
    <form onSubmit={save} data-testid="reimbursement-profile-panel" className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] space-y-4">
      <p className="text-[13px] text-white/55 mb-2">
        Tell us where to send your reimbursements. We currently support Zelle (instant, free). Bank and Stripe payouts coming soon.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Business name</span>
          <input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className={inputCls} data-testid="profile-business-name" />
        </label>
        <label className="block">
          <span className={labelCls}>Contact name</span>
          <input value={profile.contact_name} onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })} className={inputCls} data-testid="profile-contact-name" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Contact email</span>
          <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={inputCls} data-testid="profile-email" />
        </label>
        <label className="block">
          <span className={labelCls}>Phone</span>
          <input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} data-testid="profile-phone" />
        </label>
      </div>

      <div className="p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/[0.04]">
        <span className={labelCls}>Zelle email (preferred for now)</span>
        <input type="email" value={profile.zelle_email} onChange={(e) => setProfile({ ...profile, zelle_email: e.target.value })} placeholder="payouts@yourrestaurant.com"
          className="w-full px-4 py-3 rounded-xl bg-black/60 border border-yellow-500/30 focus:border-yellow-500/60 outline-none text-[14px]" data-testid="profile-zelle-email" />
        <p className="mt-2 text-[11px] text-white/45">This is where NoFee sends reimbursements (Zelle is free + instant).</p>
      </div>

      {error && <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[12.5px]" data-testid="profile-error"><AlertCircle className="w-3.5 h-3.5 inline mr-1" /> {error}</div>}
      {saved && <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200 text-[12.5px]" data-testid="profile-saved"><Check className="w-3.5 h-3.5 inline mr-1" /> Saved.</div>}

      <button type="submit" disabled={saving} data-testid="profile-save-btn"
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 disabled:opacity-50 text-black font-bold text-[13.5px]">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Save reimbursement profile
      </button>
    </form>
  );
};

export default DashboardGiftsPage;
